import { apiClient, isNotFoundError } from "@config/apiClient";
import { API_URL } from "@config/constants";
import { JSONContent } from "@app-types/common.type";

import { UnsubscribeThreadRequest, SyncEmailRequest } from "@app-types/requests.type";
import { IThreadAIContextResponse } from "@interfaces/thread.interface";
import { IEmailAttachments, IEmailResponse, IPagedResponse } from "@interfaces/responses.interface";
import { IThreadResponse } from "@interfaces/thread.interface";
import { IEmail } from "@interfaces/email.interface";

class EmailService {
  private URL = `${API_URL}/emails`;

  async getEmail(itemId: string): Promise<IEmailResponse> {
    return apiClient.get(
      `${this.URL}/add-in/get-email/?identifier=${encodeURIComponent(itemId)}`
    );
  }

  async syncEmail(data: SyncEmailRequest, signal?: AbortSignal): Promise<IEmailResponse> {
    return apiClient.post(`${this.URL}/emails/subscribe/`, data, { signal });
  }

  async removeEmail(emailId: string): Promise<{}> {
    try {
      return await apiClient.delete(`${this.URL}/emails/${emailId}/`);
    } catch (error) {
      // 404 means the email is already gone server-side — desired end-state already true
      if (isNotFoundError(error)) return {};
      throw error;
    }
  }

  async getThread(threadId: string, stringParams: string = ""): Promise<IThreadResponse> {
    return apiClient.get(`${this.URL}/threads/${threadId}/${stringParams}`);
  }

  async getThreadAttachments(threadId: string): Promise<IPagedResponse<IEmailAttachments>> {
    return apiClient.get(`${this.URL}/emails/?thread=${threadId}&query={attachments}`);
  }

  async getThreadAIContext(
    threadId: string,
    params?: URLSearchParams
  ): Promise<IThreadAIContextResponse> {
    return apiClient.get(
      `${this.URL}/threads/${threadId}/context/?${params?.toString() || ""}`
    );
  }

  async updateThreadNote(threadId: string, note_content: JSONContent): Promise<IEmail> {
    return apiClient.patch(`${this.URL}/threads/${threadId}/`, { note_content });
  }

  async unsubscribeThread(
    threadId: string,
    data: Partial<UnsubscribeThreadRequest>
  ): Promise<IThreadResponse> {
    return apiClient.patch(`${this.URL}/threads/${threadId}/`, data);
  }

  async removeThread(threadId: string): Promise<{}> {
    try {
      return await apiClient.delete(`${this.URL}/threads/${threadId}/`);
    } catch (error) {
      // 404 means the thread is already gone server-side — desired end-state already true
      if (isNotFoundError(error)) return {};
      throw error;
    }
  }
}

const emailService = new EmailService();
export default emailService;
